"use client";

/**
 * Error boundary for route-level errors.
 * Catches rendering errors in page components without crashing the layout.
 * Never exposes internal error details to the user.
 */

import { useEffect } from "react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log to console in development only — never expose to users
    if (process.env.NODE_ENV === "development") {
      console.error("Route error boundary caught:", error);
    }
  }, [error]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-error-light">
          <svg
            className="h-6 w-6 text-error"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
        </div>
        <h2 className="mb-2 text-lg font-medium text-text-primary">
          Something went wrong
        </h2>
        <p className="mb-6 text-sm text-text-secondary">
          An unexpected error occurred. Please try again or refresh the page. If
          the problem persists, contact support.
        </p>
        <button
          onClick={reset}
          className="rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-text shadow-sm hover:bg-accent-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
